// Candlestick patterns as pure OHLC math — no chart-vision mystique, just
// the standard numeric definitions, so a "pattern" either mechanically
// fired on a bar or it didn't. Series are 1/0 (null where a previous bar
// is required but absent), which lets the strategy engine treat a pattern
// like any other rule: "appears" == value > 0.5 on that bar.

import type { OHLCPoint } from "./market-data.js";

export type PatternKind =
  | "patBullEngulf"
  | "patBearEngulf"
  | "patHammer"
  | "patStar"
  | "patDoji"
  | "patInside"
  | "patOutside";

export const patternKinds: PatternKind[] = [
  "patBullEngulf",
  "patBearEngulf",
  "patHammer",
  "patStar",
  "patDoji",
  "patInside",
  "patOutside",
];

export const patternLabels: Record<PatternKind, string> = {
  patBullEngulf: "Bullish engulfing",
  patBearEngulf: "Bearish engulfing",
  patHammer: "Hammer / pin bar",
  patStar: "Shooting star",
  patDoji: "Doji",
  patInside: "Inside bar",
  patOutside: "Outside bar",
};

const body = (c: OHLCPoint) => Math.abs(c.close - c.open);
const range = (c: OHLCPoint) => c.high - c.low;
const upperWick = (c: OHLCPoint) => c.high - Math.max(c.open, c.close);
const lowerWick = (c: OHLCPoint) => Math.min(c.open, c.close) - c.low;

/** Bearish bar then a bullish bar whose BODY swallows the previous body. */
export function isBullishEngulfing(prev: OHLCPoint, cur: OHLCPoint): boolean {
  return prev.close < prev.open && cur.close > cur.open && cur.open <= prev.close && cur.close >= prev.open && body(cur) > body(prev);
}

export function isBearishEngulfing(prev: OHLCPoint, cur: OHLCPoint): boolean {
  return prev.close > prev.open && cur.close < cur.open && cur.open >= prev.close && cur.close <= prev.open && body(cur) > body(prev);
}

/** Long lower wick (≥2× body), small upper wick — rejection of the lows.
 * Direction-agnostic on the body color (both count as hammers/pins). */
export function isHammer(c: OHLCPoint): boolean {
  const r = range(c);
  if (r <= 0) return false;
  return lowerWick(c) >= 2 * body(c) && upperWick(c) <= body(c) && body(c) >= 0.03 * r;
}

/** Mirror of the hammer: long upper wick, rejection of the highs. */
export function isShootingStar(c: OHLCPoint): boolean {
  const r = range(c);
  if (r <= 0) return false;
  return upperWick(c) >= 2 * body(c) && lowerWick(c) <= body(c) && body(c) >= 0.03 * r;
}

/** Open ≈ close: indecision. Body within 10% of the bar's range. */
export function isDoji(c: OHLCPoint): boolean {
  const r = range(c);
  return r > 0 && body(c) <= 0.1 * r;
}

export function isInsideBar(prev: OHLCPoint, cur: OHLCPoint): boolean {
  return cur.high < prev.high && cur.low > prev.low;
}

export function isOutsideBar(prev: OHLCPoint, cur: OHLCPoint): boolean {
  return cur.high > prev.high && cur.low < prev.low;
}

/** 1 where the pattern fired, 0 where it didn't, null where it can't be
 * evaluated yet (two-bar patterns on the first bar). */
export function patternSeries(kind: PatternKind, candles: OHLCPoint[]): (number | null)[] {
  return candles.map((cur, i) => {
    const prev = candles[i - 1];
    switch (kind) {
      case "patBullEngulf":
        return prev ? (isBullishEngulfing(prev, cur) ? 1 : 0) : null;
      case "patBearEngulf":
        return prev ? (isBearishEngulfing(prev, cur) ? 1 : 0) : null;
      case "patInside":
        return prev ? (isInsideBar(prev, cur) ? 1 : 0) : null;
      case "patOutside":
        return prev ? (isOutsideBar(prev, cur) ? 1 : 0) : null;
      case "patHammer":
        return isHammer(cur) ? 1 : 0;
      case "patStar":
        return isShootingStar(cur) ? 1 : 0;
      case "patDoji":
        return isDoji(cur) ? 1 : 0;
    }
  });
}
